import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import './index.css';
import App from './App.tsx';
import ThemeProvider from './contexts/ThemeContext';
import AppThemeProvider from './components/AppThemeProvider';
import './i18n';
import { initializeDatabase } from './db/database';
import { useDownloadStore } from './stores/downloadStore';
import { swManager } from './services/serviceWorkerManager';
import {
  initializeSyncService,
  processAll
} from './services/offlineSyncService';
import { registerSyncHandlers } from './services/syncHandlers';
import { startMonitoring } from './services/connectivityService';
import { AuthProvider } from './contexts/AuthContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { queryClient } from './queryClient';
import * as mqttService from './services/mqttService';

const initApp = async () => {
  await initializeDatabase();
  await startMonitoring();
  // Register sync handlers and online event listener
  registerSyncHandlers();
  initializeSyncService();
  // Initialize download statuses from IndexedDB after database is ready
  await useDownloadStore.getState().initializeStatuses();
  // Register service worker for offline support (non-blocking)
  try {
    await swManager.register();
  } catch (err) {
    console.warn('[App] Service worker registration failed:', err);
  }

  const mqttBrokerUrl = import.meta.env.VITE_MQTT_BROKER_URL;
  const mqttStationId =
    import.meta.env.VITE_MQTT_STATION_ID || 'DEV-STATION-01';
  if (mqttBrokerUrl) {
    console.log(
      '[MQTT] Connecting to',
      mqttBrokerUrl,
      'station:',
      mqttStationId
    );
    const mqttOptions: Partial<import('mqtt').IClientOptions> = {};
    const mqttUsername = import.meta.env.VITE_MQTT_USERNAME;
    const mqttPassword = import.meta.env.VITE_MQTT_PASSWORD;
    if (mqttUsername) {
      mqttOptions.username = mqttUsername;
      mqttOptions.password = mqttPassword;
    }
    mqttService.connect(mqttBrokerUrl, mqttStationId, mqttOptions);
  } else {
    console.warn(
      '[MQTT] VITE_MQTT_BROKER_URL not set, skipping MQTT connection'
    );
  }

  // Process any pending sync items after app settles
  setTimeout(async () => {
    await processAll();
    const { processPendingCerts } = await import(
      './services/pendingCertService'
    );
    processPendingCerts();
  }, 3000);
};

initApp();

// Check if StrictMode should be enabled from environment variable
const enableStrictMode = import.meta.env.VITE_ENABLE_STRICT_MODE;

// Create the app element
const appElement = (
  <EnvironmentProvider>
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AppThemeProvider>
              <SnackbarProvider
                maxSnack={3}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                autoHideDuration={3000}
                action={snackbarKey => (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => closeSnackbar(snackbarKey)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              >
                <App />
              </SnackbarProvider>
            </AppThemeProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  </EnvironmentProvider>
);

// Conditionally wrap with StrictMode based on environment variable
const rootElement = enableStrictMode ? (
  <StrictMode>{appElement}</StrictMode>
) : (
  appElement
);

createRoot(document.getElementById('root')!).render(rootElement);
